import { useId, useLayoutEffect, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import {
  useClick,
  useDismiss,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import { useControllableState } from "../../utils/hooks/useControllableState";
import { useFloating } from "../dropdown-list/use-floating/UseFloating";
import { Dropdown } from "../dropdown/Dropdown";
import { ListItem } from "../list-item/ListItem";
import { DEFAULT_WIDTH, Props } from "./Select.Parts";
import { SearchInput } from "./search-input/SearchInput";
import { Trigger } from "./trigger/Trigger";
import { useSelectionVisibility } from "./use-selection-visibility/UseSelectionVisibility";

export const Select = ({
  name,
  options,
  label,
  id: idProp,
  placeholder,
  multi,
  search,
  selection: selectionProp,
  defaultSelection = [],
  onChange,
  width = `${DEFAULT_WIDTH}rem`,
  maxWidth,
  rows = 1,
  listMaxHeight,
  matchListWidth,
  disabled,
  hasError,
  floating,
  subtle = false,
  small,
  clearable = true,
  nodeId,
  onBlur,
  ref,
}: Props) => {
  // #region Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  // #endregion

  // #region Ids
  const generatedId = useId();
  const id = idProp || generatedId;
  const searchInputId = useId();
  const listboxId = useId();
  const listRef = useRef<Array<HTMLElement | null>>([]);
  // #endregion

  // #region State
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined,
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState("");
  // #endregion

  // #region Selection Logic
  const validSelectionValues = options.map(({ value }) => value);
  const [selection = defaultSelection, setSelection] = useControllableState<
    (string | number)[]
  >(
    selectionProp?.filter((s) => validSelectionValues.includes(s)),
    defaultSelection.filter((s) => validSelectionValues.includes(s)),
    onChange,
  );

  // Filtered options based on search value
  const filteredOptions =
    search && searchValue
      ? options.filter((option) =>
          String(option.label)
            .toLowerCase()
            .includes(searchValue.toLowerCase()),
        )
      : options;

  // Get label for any option
  const getLabelForValue = (value: string | number) =>
    options.find((opt) => opt.value === value)?.selectedLabel ||
    options.find((opt) => opt.value === value)?.label ||
    String(value);

  const clearSelection = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelection([]);
  };

  const { visibleSelections, overflowCount } = useSelectionVisibility({
    selections: selection,
    getLabelForValue,
    maxWidth,
    width,
    rows,
    search,
    small,
    anchorEl,
    containerWidth,
  });
  // #endregion

  // #region Handlers
  const handleOpenChange = (newIsOpen: boolean) => {
    setIsOpen(newIsOpen);
    if (!newIsOpen) {
      setActiveIndex(null);
      setTimeout(() => inputRef.current?.blur(), 1);
    }
  };

  const handleSelect = (index: number) => {
    if (filteredOptions[index].disabled) return;

    const selectedValue = filteredOptions[index].value;

    if (multi) {
      const isSelected = selection?.includes(selectedValue);
      const newValues = isSelected
        ? (selection || []).filter((v) => v !== selectedValue)
        : [...(selection || []), selectedValue];

      setSelection(newValues);
      setSearchValue("");
    } else {
      if (selection[0] === selectedValue) setSelection([]);
      else setSelection([selectedValue]);
      handleOpenChange(false);
    }
  };
  // #endregion

  // #region Floating UI Setup and Logic
  useLayoutEffect(() => {
    if (buttonRef.current) setContainerWidth(buttonRef.current.offsetWidth);
  }, [selection, maxWidth, rows]);

  const { refs, floatingStyles, context } = useFloating({
    anchor: anchorEl,
    open: isOpen,
    onOpenChange: handleOpenChange,
    offset: 16,
    matchAnchorWidth: matchListWidth,
    minWidth: !matchListWidth ? "18.75rem" : undefined,
    nodeId,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [
      useClick(context, { toggle: false }),
      useDismiss(context),
      useListNavigation(context, {
        listRef,
        activeIndex,
        onNavigate: setActiveIndex,
        virtual: true,
        loop: true,
        focusItemOnOpen: false,
      }),
      useRole(context, { role: "listbox" }),
    ],
  );

  const { ref: referenceRefBase, ...referenceProps } = getReferenceProps();
  const referenceRef = referenceRefBase as (
    node: HTMLDivElement | null,
  ) => void;
  // #endregion

  const searchInput = search ? (
    <SearchInput
      id={searchInputId}
      context={context}
      ref={mergeRefs([ref, inputRef])}
      disabled={disabled}
      active={
        selection.length > 0 || searchValue.length > 0 || Boolean(placeholder)
      }
      placeholder={placeholder}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      onBlur={onBlur}
      referenceProps={referenceProps}
      selection={selection}
      setSelection={setSelection}
      handleSelect={handleSelect}
      filteredOptions={filteredOptions}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      listboxId={listboxId}
    />
  ) : null;

  return (
    <>
      <Trigger
        // Button Element Props
        name={name}
        label={label}
        id={id}
        rows={rows}
        onBlur={onBlur}
        // Button Styling Props
        width={width}
        maxWidth={maxWidth}
        disabled={disabled}
        hasContent={
          selection.length > 0 || searchValue.length > 0 || Boolean(placeholder)
        }
        hasError={hasError}
        subtle={subtle}
        small={small}
        // Floating UI
        isOpen={isOpen}
        handleOpenChange={handleOpenChange}
        context={context}
        referenceProps={referenceProps}
        inputRef={inputRef}
        buttonRef={buttonRef}
        setAnchorEl={setAnchorEl}
        referenceRef={referenceRef}
        // Selection State & Data
        multi={multi}
        selection={selection}
        setSelection={setSelection}
        clearSelection={clearSelection}
        activeIndex={activeIndex}
        filteredOptions={filteredOptions}
        getLabelForValue={getLabelForValue}
        handleSelect={handleSelect}
        clearable={clearable}
        // Search Input
        search={search}
        searchInput={searchInput}
        searchValue={searchValue}
        searchInputId={searchInputId}
        placeholder={placeholder}
        // Selection Pills and their Visibility Logic in the Button
        visibleSelections={visibleSelections}
        overflowCount={overflowCount}
      />
      <Dropdown
        id={listboxId}
        context={context}
        refs={refs}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        maxHeight={listMaxHeight}
        initialFocus={-1}
        onMouseDown={(e) => e.preventDefault()}
        floating={floating}
      >
        {filteredOptions.map((option, index) => (
          <ListItem
            key={option.value}
            {...getItemProps({
              ref: (node: HTMLElement | null) => {
                listRef.current[index] = node;
              },
              onClick: () => handleSelect(index),
              onKeyDown: () => (context.dataRef.current.isFocusVisible = true),
              onMouseEnter: () =>
                (context.dataRef.current.isFocusVisible = false),
            })}
            id={`select-option-${option.value}`}
            isActive={index === activeIndex}
            isFocusVisible={context.dataRef.current.isFocusVisible}
            label={option.label}
            checkbox={multi}
            disabled={option.disabled}
            selected={selection.includes(option.value)}
          />
        ))}
      </Dropdown>
    </>
  );
};
